// =============================================================================
// Entrepreneur Application Funnel — central configuration
// -----------------------------------------------------------------------------
// Update funnel copy, answer options, and scoring weights in this single file.
// Everything else reads from here.
// =============================================================================

// Marks every submission so it can be filtered out of other lead sources.
export const FUNNEL_SOURCE = 'careers-funnel';

// Where qualified applicants send their resume + cover letter. Also used as the
// reply-to address for funnel emails. Update to a careers inbox if you have one.
export const CONTACT_EMAIL = 'sean@revolutiontravel.ca';

// Founder video shown in the "Watch This Before You Apply" block on the landing
// page. Leave '' to show a styled placeholder. A path ending in .mp4/.webm/.mov
// is rendered with a native <video> tag; an http(s) URL is rendered as an
// <iframe> embed (YouTube/Vimeo).
export const FOUNDER_VIDEO_URL = '/founder_video.mp4';

// -----------------------------------------------------------------------------
// Current cohort — THE ONLY PLACE COHORT DATES ARE WRITTEN
// -----------------------------------------------------------------------------
// Every deadline line on the landing page reads from here. When the next cohort
// opens, change these five strings and nothing else. Leaving a stale deadline
// on the page is the single most expensive bug in this funnel: paid traffic
// lands on an expired date and bounces before scrolling.
export const COHORT = {
  // Deadline banner under both CTAs (hero + final).
  deadline: 'Applications close September 20 for October 1 onboarding.',
  // Headline inside the "Next cohort" panel.
  nextCohort: 'Applications for the October 1 onboarding group close September 20.',
  // Used in the application form header and on the result page.
  onboarding: 'October 1 onboarding',
  closes: 'September 20',
};

// -----------------------------------------------------------------------------
// Qualification tiers
// -----------------------------------------------------------------------------
export const TIER = {
  QUALIFIED: 'qualified',
  NURTURE: 'nurture',
  NOT_FIT: 'not-fit',
};

// Score >= qualified  -> Qualified
// Score >= nurture    -> Nurture
// Otherwise           -> Not currently a fit
export const TIER_THRESHOLDS = {
  qualified: 75,
  nurture: 50,
};

export const TIER_LABELS = {
  [TIER.QUALIFIED]: 'Qualified',
  [TIER.NURTURE]: 'Nurture',
  [TIER.NOT_FIT]: 'Not currently a fit',
};

// -----------------------------------------------------------------------------
// Lead quality flags — automatically derived from answers (see scoring.js).
// `tone: 'positive'` is an encouraging signal; `'negative'` is a caution signal.
// Order here is the order flags are displayed on the admin review page.
// -----------------------------------------------------------------------------
export const LEAD_FLAGS = {
  strong_sales_background: { label: 'Strong sales background', tone: 'positive' },
  entrepreneurial_background: { label: 'Entrepreneurial background', tone: 'positive' },
  strong_written_answers: { label: 'Strong written answers', tone: 'positive' },
  travel_perks_focus: { label: 'Focused on travel perks', tone: 'negative' },
  hobby_language: { label: 'Hobby / casual language', tone: 'negative' },
  weak_motivation: { label: 'Weak motivation', tone: 'negative' },
  low_time_commitment: { label: 'Low time commitment', tone: 'negative' },
  wants_salary: { label: 'Wants a salaried job', tone: 'negative' },
  avoids_lead_generation: { label: 'Avoids lead generation', tone: 'negative' },
};

/** Readable label for a lead quality flag key. */
export function formatFlag(key) {
  return (LEAD_FLAGS[key] && LEAD_FLAGS[key].label) || key;
}

// -----------------------------------------------------------------------------
// Scoring weights — see scoring.js for how these are applied
// -----------------------------------------------------------------------------
export const SCORE_WEIGHTS = {
  goalType: { business: 20, 'side-hustle': 8, job: -20 },
  salesExperience: { yes: 20, no: 0 },
  businessExperience: { yes: 15, no: 0 },
  comfortableLeads: { yes: 20, somewhat: 8, no: 0 },
  hoursPerWeek: { 'lt-10': 0, '10-14': 10, '15-24': 15, '25-plus': 15 },
  // Income ambition is not in the original spec's suggested table; it is added
  // as a light signal of seriousness and can be zeroed out here if unwanted.
  incomeGoal: { 'lt-25k': 0, '25k-50k': 5, '50k-100k': 8, '100k-plus': 8 },
  understandsCommission: { yes: 20, no: 0 },
  strongMotivation: 10,
  weakMotivation: -15,
};

// -----------------------------------------------------------------------------
// Answer options for the application form
// -----------------------------------------------------------------------------
export const GOAL_OPTIONS = [
  { value: 'business', label: 'A business opportunity', hint: 'I want to build a real business of my own.' },
  { value: 'side-hustle', label: 'A side hustle', hint: 'Something to grow alongside other commitments.' },
  { value: 'job', label: 'A traditional job', hint: 'A salaried role with set hours and a fixed paycheque.' },
];

export const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const LEADS_OPTIONS = [
  { value: 'yes', label: 'Yes — I am confident doing this', hint: 'Relationships, referrals, content, networking, outreach.' },
  { value: 'somewhat', label: 'Somewhat — I would need coaching', hint: 'Willing to learn, but it is new to me.' },
  { value: 'no', label: 'No — I would rather be handed leads', hint: 'I prefer leads provided for me.' },
];

export const HOURS_OPTIONS = [
  { value: 'lt-10', label: 'Less than 10 hours / week' },
  { value: '10-14', label: '10 – 14 hours / week' },
  { value: '15-24', label: '15 – 24 hours / week' },
  { value: '25-plus', label: '25+ hours / week' },
];

export const INCOME_OPTIONS = [
  { value: 'lt-25k', label: 'Under $25,000' },
  { value: '25k-50k', label: '$25,000 – $50,000' },
  { value: '50k-100k', label: '$50,000 – $100,000' },
  { value: '100k-plus', label: '$100,000+' },
];

// -----------------------------------------------------------------------------
// Human-readable question labels (used by the admin review page)
// -----------------------------------------------------------------------------
export const QUESTION_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  location: 'City / Province',
  goalType: 'Are you looking for a job, side hustle, or business opportunity?',
  salesExperience:
    'Have you worked in sales, business development, real estate, insurance, recruiting, financial services, or another commission-based role?',
  businessExperience: 'Have you ever run a business, side hustle, or freelance service?',
  comfortableLeads:
    'Are you comfortable generating your own leads through relationships, referrals, content, networking, or outreach?',
  hoursPerWeek: 'How many hours per week can you realistically commit?',
  incomeGoal: 'What income goal would make this opportunity worth your time over the next 12–24 months?',
  understandsCommission: 'Do you understand this is a commission-based opportunity, not a salaried position?',
  whyInterested: 'Why are you interested in building a travel business?',
  successReason: 'What makes you believe you would succeed in a commission-based entrepreneurial role?',
};

// Order applicant answers are displayed in the admin detail view.
export const ANSWER_ORDER = [
  'goalType',
  'salesExperience',
  'businessExperience',
  'comfortableLeads',
  'hoursPerWeek',
  'incomeGoal',
  'understandsCommission',
  'whyInterested',
  'successReason',
];

// Lookup table to render stored option values as readable text.
const VALUE_LABELS = {};
[GOAL_OPTIONS, LEADS_OPTIONS, HOURS_OPTIONS, INCOME_OPTIONS, YES_NO_OPTIONS].forEach((group) => {
  group.forEach((opt) => {
    VALUE_LABELS[opt.value] = VALUE_LABELS[opt.value] || opt.label;
  });
});

/** Convert a stored answer value into human-readable text for display. */
export function formatAnswer(value) {
  if (value === null || value === undefined || value === '') return '—';
  return VALUE_LABELS[value] || value;
}
